import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { messageService } from "@/services/altingross";

// ─── Thunks ─────────────────────────────────────────────────────────────────
export const fetchMessages = createAsyncThunk(
  "messages/fetchList",
  async ({ page = 1, size = 20, field, value, operator } = {}, { rejectWithValue }) => {
    try {
      const res = await messageService.getList({ page, size, field, value, operator });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const fetchMessageById = createAsyncThunk(
  "messages/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await messageService.getById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const answerMessage = createAsyncThunk(
  "messages/answer",
  async ({ id, responseNote }, { rejectWithValue }) => {
    try {
      await messageService.answer(id, responseNote);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const confirmMessage = createAsyncThunk(
  "messages/confirm",
  async (id, { rejectWithValue }) => {
    try {
      await messageService.confirm(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  "messages/delete",
  async (id, { rejectWithValue }) => {
    try {
      await messageService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────
const messagesSlice = createSlice({
  name: "messages",
  initialState: {
    list: [],
    pagination: { page: 1, size: 20, count: 0, totalPages: 1, hasNext: false, hasPrev: false },
    detail: null,
    loading: false,
    detailLoading: false,
    error: null,
    answeringId: null,
    confirmingId: null,
    deletingId: null,
  },
  reducers: {
    clearDetail(state) { state.detail = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchMessages
      .addCase(fetchMessages.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        const { items, page, size, count, totalPages, hasNext, hasPrev } = action.payload;
        state.list = items ?? [];
        state.pagination = { page, size, count, totalPages, hasNext, hasPrev };
      })
      .addCase(fetchMessages.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // fetchMessageById
      .addCase(fetchMessageById.pending, (state) => { state.detailLoading = true; state.detail = null; })
      .addCase(fetchMessageById.fulfilled, (state, action) => { state.detailLoading = false; state.detail = action.payload; })
      .addCase(fetchMessageById.rejected, (state) => { state.detailLoading = false; })

      // answerMessage
      .addCase(answerMessage.pending, (state, action) => { state.answeringId = action.meta.arg.id; })
      .addCase(answerMessage.fulfilled, (state) => { state.answeringId = null; })
      .addCase(answerMessage.rejected, (state) => { state.answeringId = null; })

      // confirmMessage
      .addCase(confirmMessage.pending, (state, action) => { state.confirmingId = action.meta.arg; })
      .addCase(confirmMessage.fulfilled, (state) => { state.confirmingId = null; })
      .addCase(confirmMessage.rejected, (state) => { state.confirmingId = null; })

      // deleteMessage
      .addCase(deleteMessage.pending, (state, action) => { state.deletingId = action.meta.arg; })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.deletingId = null;
        state.list = state.list.filter((m) => m.id !== action.payload);
        if (state.detail?.id === action.payload) state.detail = null;
      })
      .addCase(deleteMessage.rejected, (state) => { state.deletingId = null; });
  },
});

export const { clearDetail } = messagesSlice.actions;
export default messagesSlice.reducer;
